import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, Text, Button, Checkbox } from '@belong/ui';
import Icon, { ICONS } from 'components/Icon/Icon';
import { toUpper } from 'es-toolkit/compat';
import { updateUtilitySetup } from 'store/redux/renter-accounts/actions';
import { showToast } from 'store/redux/toast';

// `ICONS` is a literal with ~100 named keys and no index signature, so an
// upper-cased utility name cannot be used to index it directly. The `?.` was
// already there for names that miss.
const getIcon = (value: string) =>
  (ICONS as Record<string, { DEFAULT: string } | undefined>)[toUpper(value)]
    ?.DEFAULT;

type UtilityData = {
  responsibility?: string;
  utilityBillingManagedBy?: string;
  hasResidentSetup?: boolean;
  providerInstructionsId?: number;
  providerInstructions?: string;
  provider?: string;
  websiteLink?: string;
};

type UtilitiesModalProps = {
  isOpen: boolean;
  onDismiss: () => void;
  utilityTitle: string;
  propertyAddress: string;
  utilityIcon?: string;
  utilityData?: UtilityData;
  leaseUniqueId: string;
};

export function UtilitiesModal({
  isOpen,
  onDismiss,
  utilityTitle,
  propertyAddress,
  utilityIcon,
  utilityData,
  leaseUniqueId,
}: UtilitiesModalProps) {
  const dispatch = useDispatch();
  const [isUtilityTransferred, setIsUtilityTransferred] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsUtilityTransferred(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  async function handleDone() {
    if (!leaseUniqueId || !utilityTitle) return;

    setIsSubmitting(true);
    try {
      await dispatch(updateUtilitySetup(leaseUniqueId, utilityTitle));
      onDismiss();
      dispatch(
        showToast(
          { message: `${utilityTitle} Setup Completed`, created: true },
          { autoDelete: true, autoDeleteTime: 6000 },
        ),
      );
    } catch (error) {
      const errorMessage = 'An error occurred. Please try again.';
      dispatch(
        showToast(
          { message: errorMessage, error: true },
          { autoDelete: true, autoDeleteTime: 5000 },
        ),
      );
      setIsSubmitting(false);
    }
  }

  const utilityInfoItems = [];

  if (utilityData?.provider) {
    utilityInfoItems.push({ label: 'Provider', value: utilityData.provider });
  }

  if (utilityData?.responsibility) {
    utilityInfoItems.push({
      label: 'Responsibility',
      value: utilityData.responsibility.replace(/([A-Z])/g, ' $1').trim(),
    });
  }

  if (utilityData?.utilityBillingManagedBy) {
    utilityInfoItems.push({
      label: 'Billing Managed By',
      value: utilityData.utilityBillingManagedBy,
    });
  }

  return (
    <Modal
      name="utilities-modal"
      isOpen={isOpen}
      onDismiss={onDismiss}
      size="default"
      enableOverflow
      overlayClassName="z-fullscreen"
    >
      <div className=" flex w-full flex-col items-center p-2sm pt-2xl md:p-2xl md:pt-3xl">
        {/* Icon centered above title */}
        {utilityIcon && (
          <div className="mb-lg flex items-center justify-center">
            <Icon icon={getIcon(utilityIcon)} />
          </div>
        )}

        {/* Title */}
        <Text variant="h2" fontWeight="semibold" className="mb-xs text-center">
          Setup {utilityTitle}
        </Text>

        {/* Subtitle with property address */}
        <Text className="mb-0 text-center text-navy">{propertyAddress}</Text>

        {utilityInfoItems.length > 0 && (
          <div className="my-2xl flex w-full flex-col gap-2xs">
            {utilityInfoItems.map((item, idx) => (
              <div
                key={idx}
                className="my-xs flex w-full flex-col justify-between  md:my-0 md:flex-row"
              >
                <Text
                  variant="body"
                  className="truncate font-semibold text-navy md:font-regular"
                >
                  {item.label}
                </Text>
                <Text variant="body" className="text-navy">
                  {item.value}
                </Text>
              </div>
            ))}
          </div>
        )}

        {!utilityData?.hasResidentSetup &&
          utilityData?.utilityBillingManagedBy === 'Provider' && (
            <>
              {utilityData?.providerInstructions && (
                <div className="border-1 mb-2xl w-full rounded border-solid border-gray bg-light-gray p-md">
                  <Text variant="body" className="text-navy">
                    <strong>Instructions</strong>
                    <br />
                    {utilityData.providerInstructions
                      .replace(/\\n/g, '\n')
                      .split('\n')
                      .map((line, index, array) => (
                        <span key={index}>
                          {line}
                          {index < array.length - 1 && <br />}
                        </span>
                      ))}
                  </Text>
                  {utilityData.websiteLink && (
                    <div className="mx-xs mt-sm flex flex-row justify-end border-0 border-t border-dashed border-gray pt-md">
                      <a
                        href={utilityData.websiteLink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Text variant="body" className="text-navy">
                          <strong>Visit Website</strong>
                          <br />
                        </Text>
                      </a>
                    </div>
                  )}
                </div>
              )}
              {/* Checkbox */}
              <div className="mb-2xl flex w-full justify-center whitespace-nowrap">
                <Checkbox
                  id="utility-transferred"
                  label={"I'VE TRANSFERRED THIS\u00A0UTILITY"}
                  checked={isUtilityTransferred}
                  onChange={setIsUtilityTransferred as (value: boolean) => void}
                />
              </div>

              {/* Done Button */}
              <Button
                onClick={handleDone}
                disabled={!isUtilityTransferred || isSubmitting}
                className="hidden w-full md:block"
                size="large"
              >
                {isSubmitting ? 'Saving...' : 'Done'}
              </Button>
              <div className="md:hidden">
                <Button
                  onClick={handleDone}
                  disabled={!isUtilityTransferred || isSubmitting}
                  className="fixed inset-x-0 bottom-0 h-2xl w-full rounded-none"
                  size="large"
                >
                  {isSubmitting ? 'Saving...' : 'Done'}
                </Button>
              </div>
            </>
          )}
      </div>
    </Modal>
  );
}
